import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import PlusIcon from '@/assets/svg/bx-plus.svg?react';
import { Button } from '@/components/buttons';
import { ButtonGroup } from '@/components/buttons/ButtonGroup';
import { Field, FieldError } from '@/components/Form/fields';
import { Panel } from '@/components/panels';
import { isDomainValid } from '@/helper/validator';

import AIVisibilityCompetitorNameDialog from './AIVisibilityCompetitorNameDialog';
import CompetitorCell from './CompetitorCell';
import { submitOnEnter } from './submitOnEnter';
import { MAX_COMPETITORS, useAIVisibilitySettingsForm } from './useAIVisibilitySettingsForm';

const CompetitorsPanel = () => {
  const form = useAIVisibilitySettingsForm();
  const [pendingCompetitorDomain, setPendingCompetitorDomain] = useState<string | null>(null);
  const [competitorDomainError, setCompetitorDomainError] = useState<string | null>(null);
  const invalidDomainMessage = t`Values should be valid domain names (e.g. example.com), with no URL paths (e.g. example.com/path), and must not start with "http" or "www".`;

  return (
    <form.AppField name="competitors" mode="array">
      {(competitorsField) => {
        const competitors = competitorsField.state.value;
        const canAddCompetitor = competitors.length < MAX_COMPETITORS;

        const handleConfirmCompetitor = (name: string) => {
          if (
            pendingCompetitorDomain == null ||
            competitors.length >= MAX_COMPETITORS ||
            competitors.some(
              (competitor) =>
                competitor.domain.toLowerCase() === pendingCompetitorDomain.toLowerCase(),
            )
          ) {
            return;
          }

          competitorsField.pushValue({
            name,
            domain: pendingCompetitorDomain,
            comparedProductAreas: [...form.getFieldValue('brandProductAreas')],
          });
          form.setFieldValue('competitorDomain', '');
          setPendingCompetitorDomain(null);
        };

        return (
          <Panel
            title={t`Competitors`}
            subtitle={<Trans>We'll compare your brand against these companies.</Trans>}
          >
            {canAddCompetitor && (
              <Field
                aria-label={t`Competitors`}
                data-invalid={competitorDomainError ? true : undefined}
              >
                <form.AppField
                  name="competitorDomain"
                  listeners={{
                    onChange: () => setCompetitorDomainError(null),
                  }}
                >
                  {(competitorDomainField) => {
                    const handleAddCompetitor = () => {
                      if (!canAddCompetitor) {
                        return;
                      }

                      const nextDomain = competitorDomainField.state.value.trim();
                      if (!isDomainValid(nextDomain)) {
                        setCompetitorDomainError(invalidDomainMessage);
                        return;
                      }

                      const isDuplicate = competitors.some(
                        (competitor) =>
                          competitor.domain.toLowerCase() === nextDomain.toLowerCase(),
                      );
                      if (isDuplicate) {
                        setCompetitorDomainError(t`This competitor has already been added.`);
                        return;
                      }

                      setCompetitorDomainError(null);
                      setPendingCompetitorDomain(nextDomain);
                    };

                    return (
                      <form.Subscribe selector={(state) => state.isSubmitting}>
                        {(isSubmitting) => (
                          <ButtonGroup className="w-full">
                            <competitorDomainField.TextInput
                              id="ai-visibility-competitor-domain"
                              size="large"
                              aria-label={t`Competitors`}
                              placeholder={t`Enter competitor domain...`}
                              onKeyDown={submitOnEnter(handleAddCompetitor)}
                            />
                            <Button
                              size="medium"
                              className="h-10"
                              disabled={isSubmitting}
                              onClick={handleAddCompetitor}
                            >
                              <PlusIcon />
                              <Trans>Add</Trans>
                            </Button>
                          </ButtonGroup>
                        )}
                      </form.Subscribe>
                    );
                  }}
                </form.AppField>
                {competitorDomainError && (
                  <FieldError errors={[{ message: competitorDomainError }]} />
                )}
              </Field>
            )}
            {competitors.length > 0 && (
              <div className="flex flex-col gap-4">
                {competitors.map((competitor, competitorIndex) => (
                  <CompetitorCell
                    key={competitor.domain}
                    competitorIndex={competitorIndex}
                    onDelete={() => competitorsField.removeValue(competitorIndex)}
                  />
                ))}
              </div>
            )}
            {pendingCompetitorDomain != null && (
              <AIVisibilityCompetitorNameDialog
                domain={pendingCompetitorDomain}
                onCancel={() => setPendingCompetitorDomain(null)}
                onConfirm={handleConfirmCompetitor}
              />
            )}
          </Panel>
        );
      }}
    </form.AppField>
  );
};

export default CompetitorsPanel;
